import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { post, get, patch } from '../../../../helpers/httpClient';
import { toast } from 'react-toastify';
import { renderEditorDataToHTML } from '../../../../helpers/Editor';
import RichTextEditor, { safeParseBody } from '../../../../helpers/RichTextEditor';
import PageBasicFields from '../../../../helpers/PageBasicFields';

function ArticleEditor() {
  const navigate = useNavigate();
  const { id, pageId } = useParams();
  const [searchParams] = useSearchParams();
  const isEdit = !!pageId;

  const [form, setForm] = useState({ slug: '', title: '', extra: { body: { blocks: [] } } });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!pageId);

  const titleText = useMemo(() => (isEdit ? 'Edit Article' : 'New Article'), [isEdit]);

  const handleEditorChange = (data) => {
    setForm((prev) => ({
      ...prev,
      extra: {
        ...(prev.extra || {}),
        content: { ...(prev.extra?.content || {}), body: data },
      },
    }));
  };

  useEffect(() => {
    let isMounted = true;
    async function loadPage() {
      if (!isEdit) return;
      try {
        setLoading(true);
        setError('');
        const resp = await get(`pages/${pageId}`);
        const page = resp?.page || resp;
        if (!page) throw new Error('Not found');
        if (!isMounted) return;
        setForm({
          slug: page.slug || '',
          title: page.title || '',
          description: page.description || '',
          caption: page.caption || '',
          preview: page.preview,
          template: page.template || null,
          components: page.components || { items: [] },
          extra: page.extra || {},
        });
      } catch (e) {
        if (isMounted) toast.error('Failed to load page data.');
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    loadPage();
    return () => { isMounted = false; };
  }, [id, pageId, isEdit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.title.trim()) {
      toast.error('Title is required.');
      return;
    }

    const bodyObj = safeParseBody(form?.extra?.content?.body);
    const htmlEditor = JSON.stringify(bodyObj);

    const payload = {
      type: 'article',
      slug: (form.slug || '').trim(),
      title: (form.title || '').trim(),
      description: (form.description || '').trim() || null,
      caption: (form.caption || '').trim() || null,
      preview: form.preview || null,
      template: form.template || null,
      components: form.components || [],
      extra: {
          body: htmlEditor,
          rendered: renderEditorDataToHTML(bodyObj),
      },
    };

    try {
      setSaving(true);
      setError('');
      if (isEdit) {
        await patch(`websites/${id}/pages/${pageId}`, payload);
      } else {
        const parent = searchParams.get('parent');
        await post(`websites/${id}/pages`, { ...payload, order: 0, parent: parent || null });
      }
      navigate(`/websites/${id}?tab=structure`);
    } catch (err) {
      toast.error(isEdit ? 'Failed to update page.' : 'Failed to create page. Please fix validation issues or try a different slug.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => navigate(-1);

  return (
    <Container className="mt-4">
      <Row>
        <Col>
              <div className="structure-node-header mb-3">
                <h2 className="m-0">{titleText}</h2>
                <span className="page-type-badge page-type-badge--catalog">Article</span>
              </div>

              {error && <Alert variant="danger">{error}</Alert>}

              {loading ? (
                <div className="text-center my-3"><Spinner animation="border" /></div>
              ) : (
                <Form onSubmit={handleSubmit}>
                  <PageBasicFields
                    form={form}
                    setForm={setForm}
                    websiteId={id}
                    slugPlaceholder="e.g. my-first-post"
                    titlePlaceholder="Enter title"
                    preserveEmptySlug={isEdit}
                  />

                  <div className="props-panel">
                    <h6 className="theme-group-label mb-3">Content</h6>
                    <RichTextEditor value={form.extra?.content?.body} onChange={handleEditorChange} websiteId={id} />
                  </div>

                  <div className="form-actions">
                    <Button type="submit" className="primary-button" disabled={saving}>
                      {saving ? (<><Spinner animation="border" size="sm" className="me-2" /> Saving...</>) : 'Save'}
                    </Button>
                    <Button className="primary-button" onClick={handleCancel} disabled={saving}>Cancel</Button>
                  </div>
                </Form>
              )}
        </Col>
      </Row>
    </Container>
  );
}

export default ArticleEditor;
